import { addSkuToBasket, BASKET_CLIENT_ID } from "./basketApi";
import {
  AddToBasketChildItemRequest,
  AddToBasketResponse,
  APIErrorCode,
  SubmissionContext,
  SubmissionResult,
  BasketSubmissionParams,
  BasketSubmissionCallbacks,
} from "../types";
import { getBasketItems } from "@/components/AddToBasket/AddToBasketLocal/utils/getBasketItems.ts";
import {
  updateBasketAnalyticsObject,
  updateExpressAnalytics,
  addToBasketSuccessPayload,
  ProductInfo,
  buildBasketUpdateDataFromBasket,
  buildTrackEventProductData,
} from "@/components/AddToBasket/AddToBasketLocal/utils/analyticsHelpers.ts";
import { fireMonetateEventIfReferredByPLP } from "@/components/AddToBasket/AddToBasketLocal/utils/monetateHelpers.ts";
import { dispatchCustomEvent } from "@/utils/customEvent/dispatchCustomEvent.ts";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ.ts";
import {
  AddToBasket,
  trackEvent,
} from "@JohnLewisPartnership/jl-analytics-event-stream";
import { addPageAction, noticeError } from "@/utils/newrelic/requests.ts";
import Cookies from "js-cookie";
import { sha3_256 } from "js-sha3";
import { dispatchAnalyticsEvent } from "@/utils";
import { ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED } from "@/constants/constants.ts";

const RESET_DELAY_MS = 300;
const MAX_RETRIES = 5;

/**
 * Default hooks for basket submission lifecycle.
 * These provide logging points for tracking submission events.
 * Override these to add custom event dispatching or monitoring.
 */
const defaultSubmissionHooks = {
  onStart: (_context: SubmissionContext) => {
    // void
  },
  onSubmitted: (_context: SubmissionContext) => {
    if (_context.isExpressCheckout) {
      updateExpressAnalytics();
    } else {
      updateBasketAnalyticsObject();
    }

    dispatchCustomEvent("ADD_TO_BASKET_ESI:BASKET_SUBMITTED");
  },
  onSuccess: (_result: SubmissionResult) => {
    dispatchCustomEvent(ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED, {
      errors: _result.errors,
    });

    fireMonetateEventIfReferredByPLP();

    const addedItem = (_result.commerceItems || []).find(
      (item) => item.skuId === _result.skuId,
    );

    if (!addedItem) {
      return;
    }

    const productInfo: ProductInfo = {
      productId: _result.productId,
      price: _result.price ?? addedItem?.price,
      title: _result.title ?? addedItem?.name,
      skuId: _result.skuId,
      variantId: _result.variantId,
      qty: _result.quantity,
      childItems: addedItem?.childItems,
    };

    dispatchAnalyticsEvent(
      "addtobasketsuccess",
      addToBasketSuccessPayload(
        productInfo,
        _result.isExpressCheckout,
        _result.basketContent,
      ),
    );

    const basketData = buildBasketUpdateDataFromBasket(_result.basketContent);

    trackEvent(AddToBasket, {
      originatingImplementationId: "web-pdp-scaffold-ui",
      triggeringImplementationId: "web-pdp-scaffold-ui",
      type: _result.isExpressCheckout ? "shipfromstore" : "standard",
      buttonType: _result.isExpressCheckout ? "expresscheckout" : "static",
      products: [
        buildTrackEventProductData(
          _result.productId,
          _result.title,
          _result.quantity,
          _result.skuId,
          addedItem,
        ),
      ],
      page: {
        name: window?.jlData?.page?.pageInfo?.pageName,
        type: "pdp scaffold product",
      },
      basket: {
        ...basketData,
        subTotal: 0,
      },
    });

    if (!_result.isExpressCheckout) {
      dispatchCustomEvent("jl-events.browser.basketQuantityUpdated");
    }

    addPageAction("basketSubmissionSuccess", {
      orderId: _result.orderId,
      basketContent: _result.basketContent?.length,
      retryCount: _result.retryCount + 1,
    });
  },
  onError: (_result: SubmissionResult) => {
    dispatchCustomEvent("ADD_TO_BASKET_ESI:WARNING_OR_ERROR", {
      errors: _result.errors,
    });
  },
  onRetry: (
    _context: SubmissionContext,
    _attemptNumber: number,
    errorMessage: string,
  ) => {
    addPageAction("add-to-basket-retries", {
      retryCount: _attemptNumber,
    });

    noticeError(
      `Error from basket service. Error: ${errorMessage}, Retry Count: ${_attemptNumber}`,
    );
  },
};

export const handleBasketSubmission = async (
  {
    basketApiBaseUrl,
    basketCheckoutHost,
    pimProductId,
    skuId,
    variantId,
    isExpressCheckout,
    isShipFromStore,
    price,
    title,
    services,
  }: BasketSubmissionParams,
  {
    setIsSubmitting,
    setIsExpressSubmitting,
    setAddToBasketSubmissionError,
    setBasket,
    setButtonState,
    focusAddToBasketButton,
  }: BasketSubmissionCallbacks,
) => {
  const context: SubmissionContext = {
    productId: pimProductId,
    skuId,
    variantId,
    isExpressCheckout,
    isShipFromStore,
  };

  const childItems: AddToBasketChildItemRequest[] = services
    .filter((service) => service.selected)
    .map((service) => ({
      variantId: service.id,
      quantity: 1,
    }));

  defaultSubmissionHooks.onStart(context);

  if (isExpressCheckout) {
    setIsExpressSubmitting(true);
    dispatchMonetateQEvent("express_checkout_click");
  } else {
    setIsSubmitting(true);
    dispatchMonetateQEvent("add_to_basket_click");
  }

  let retryCount = 0;
  let finalResult: AddToBasketResponse = {
    success: false,
    errors: [],
    commerceItems: [],
  };

  for (retryCount; retryCount < MAX_RETRIES; retryCount++) {
    await new Promise<void>((resolve) => {
      setTimeout(() => resolve(), retryCount * 500);
    });

    finalResult = await addSkuToBasket({
      basketApiBaseUrl,
      skuId,
      variantId,
      quantity: 1,
      childItems,
      express: isExpressCheckout,
      isShipFromStore,
    });

    const { success, responseStatus } = finalResult;

    const shouldStopRetrying =
      success || (!success && responseStatus === 422) || retryCount === 4;

    if (shouldStopRetrying) {
      break;
    }

    defaultSubmissionHooks.onRetry(
      context,
      retryCount + 1,
      finalResult.errorMessage || "Generic error",
    );
  }

  defaultSubmissionHooks.onSubmitted(context);

  window.setTimeout(() => {
    setIsSubmitting(false);
    setIsExpressSubmitting(false);
  }, RESET_DELAY_MS);

  const basketContent = getBasketItems(finalResult.commerceItems, pimProductId);

  const submissionResult: SubmissionResult = {
    ...context,
    success: finalResult.success ?? false,
    orderId: finalResult.orderId,
    retryCount,
    commerceItems: finalResult.commerceItems,
    basketContent,
    errors: finalResult.errors,
    quantity: 1,
    price,
    title,
  };

  if (finalResult.success) {
    defaultSubmissionHooks.onSuccess(submissionResult);

    setAddToBasketSubmissionError(undefined);

    if (basketContent) {
      setBasket(basketContent);
    }

    if (!isExpressCheckout) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            focusAddToBasketButton();
          }, 150);
        });
      });
    }

    if (isExpressCheckout) {
      window.location.assign(
        `${basketCheckoutHost}/web/handover?basketId=${finalResult.orderId}&express=true`,
      );
    }

    return;
  }

  const firstError = finalResult.errors[0];

  defaultSubmissionHooks.onError(submissionResult);

  setAddToBasketSubmissionError(firstError || {});

  if (firstError?.code) {
    const shopperId = sha3_256(Cookies.get("shopperId") || "Guest User");
    const errorDetails = {
      shopperId,
      productId: context.productId,
      skuId,
      variantId,
      qty: 1,
      title,
      errors: JSON.stringify(finalResult.errors),
      errorCode: finalResult.errors[0]?.code,
      errorMessage: finalResult.errors[0]?.message,
      responseStatus: finalResult.responseStatus,
      type: "pdp:addToBasket",
      upstream: finalResult.errors[0]?.originSystem,
      clientId: BASKET_CLIENT_ID,
    };

    if (firstError.code === APIErrorCode.OUT_OF_STOCK_ONLINE) {
      addPageAction("a2b-disabled", errorDetails);
      setButtonState({
        addToBasketDisabled: true,
        expressCheckoutDisabled: true,
        buttonText: "Out of stock",
      });
    } else if (
      [
        APIErrorCode.STOCK_EXCEEDED_ERROR,
        APIErrorCode.MAX_PURCHASE_QUANTITY_EXCEEDED_WARNING,
      ].includes(firstError.code)
    ) {
      if (!isExpressCheckout) {
        addPageAction("a2b-disabled", {
          shopperId,
          productId: context.productId,
          skuId,
          variantId,
          qty: 1,
          title,
          errors: JSON.stringify(finalResult.errors),
          errorCode: finalResult.errors[0]?.code,
          errorMessage: finalResult.errors[0]?.message,
          responseStatus: finalResult.responseStatus,
          type: "pdp:addToBasket",
          upstream: finalResult.errors[0]?.originSystem,
          clientId: BASKET_CLIENT_ID,
        });
        setButtonState((prevState) => ({
          ...prevState,
          addToBasketDisabled: true,
        }));
      }
    } else if (
      [
        "MAX_PURCHASE_QUANTITY_EXCEEDED_ERROR",
        "LINE_ITEMS_EXCEEDS_MAX",
        "INVALID_PRODUCT_TYPE",
        "PRODUCT_NOT_AVAILABLE_FOR_ORDERING",
        "PRODUCT_NOT_FOUND",
        "QUANTITY_MIN_INVALID",
        "STOCK_EXCEEDED_ERROR",
      ].includes(firstError.code)
    ) {
      addPageAction("a2b-disabled", errorDetails);
      setButtonState((prevState) => ({
        ...prevState,
        addToBasketDisabled: true,
        expressCheckoutDisabled: true,
      }));
    } else if (firstError.code === "STOCK_SOURCE_MISMATCH") {
      addPageAction("a2b-disabled", errorDetails);
      setButtonState((prevState) => ({
        ...prevState,
        expressCheckoutDisabled: true,
      }));
    }
  }
};
